/**
 * The landing's query parameters and the ad creative its `utm_content` names, as the
 * rule operands `var.system.originQuery.<param>` and `var.system.creative.<field>`.
 *
 * Both are read from `system.originQueryString` — the landing of the current run —
 * every time, never stored: the visibility-rule tester drives every one of them by
 * setting that string, and an order's rawData does not grow a copy of each.
 *
 * `parseCreativeName` reads an ad name as `parseUtmContentName` in the ingress repo
 * (api/src/modulegw/utils/rule-set-router-helpers.ts) does, so an ingress rule set and a
 * visibility rule read one ad name the same way. Keep the layouts, the field names and
 * the prefix list in step with it. Unlike ingress, it reads only names in the convention
 * (see parseCreativeName), and it drops the `_ingress_id_…_` suffix the gateway appends
 * to the param after it has routed (GW_QUERY_EXTEND_PARAM).
 */

/** Positional fields of an ad name, in ingress's `UTM_CONTENT_FIELDS` order. */
export const CREATIVE_FIELDS = [
  'brand',
  'angle',
  'for_whom',
  'for_what',
  'message',
  'language',
  'year_week',
  'designer',
  'brief_hook',
  'channel',
  'ratio',
  'format',
  'creative_iteration',
  'body_id',
  'length',
  'geography',
  'creative_type',
] as const
export type CreativeField = (typeof CREATIVE_FIELDS)[number]

/** Static and animated ads; motion ads add `body_id` and `length` before `geography`. */
const STATIC_LAYOUT: readonly CreativeField[] = CREATIVE_FIELDS.filter(
  (f) => f !== 'body_id' && f !== 'length',
)
const MOTION_LAYOUT: readonly CreativeField[] = CREATIVE_FIELDS
const FORMAT_INDEX = 11
const MOTION_FORMAT = 'moti'
/** The convention's formats; the 12th part of every ad name is one of them. */
export const CREATIVE_FORMATS: ReadonlySet<string> = new Set(['stat', 'anim', MOTION_FORMAT])

/** Tracking namespaces some channels put before the ad name; an allow-list, as in ingress. */
export const CREATIVE_LEADING_PREFIXES: ReadonlySet<string> = new Set(['nmcw'])

const GATEWAY_SUFFIX = /_ingress_id_\d+_ingress_rule_set_id_\d+_destination_id_\d+_/g

export type CreativeName = Partial<Record<CreativeField, string>>

/**
 * The fields an ad name holds; none when `utm_content` is not an ad name in the convention,
 * one with a format as its 12th part. Ingress reads any value by position, which turns an
 * email's message name or an older naming into an angle, a brand and so on. Kept from
 * ingress: a name cut short after its format gives the fields it has.
 *
 * The name ends at its first space, which drops the ` | …` metadata as ingress does, and
 * the ` – Copy` or ` (1)` Facebook adds to a duplicated ad.
 */
export function parseCreativeName(raw: string): CreativeName {
  const name = raw.trim().replace(GATEWAY_SUFFIX, '').split(' ')[0] ?? ''
  let parts = name.split('_')
  while (parts.length > 0 && CREATIVE_LEADING_PREFIXES.has(parts[0]!)) parts = parts.slice(1)
  const format = parts[FORMAT_INDEX]
  if (format === undefined || !CREATIVE_FORMATS.has(format)) return {}
  const layout = format === MOTION_FORMAT ? MOTION_LAYOUT : STATIC_LAYOUT
  const out: CreativeName = {}
  for (let i = 0; i < layout.length && i < parts.length; i++) out[layout[i]!] = parts[i]!
  return out
}

/** The variable holding the landing's query string, `?` included. */
export const ORIGIN_QUERY_STRING_KEY = 'system.originQueryString'
export const ORIGIN_QUERY_PREFIX = 'system.originQuery.'
export const CREATIVE_PREFIX = 'system.creative.'

/** The UTM parameters the editor offers by name; any other parameter can be typed. */
export const LANDING_UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

interface Landing {
  params: URLSearchParams
  creative: CreativeName
}

let lastRaw: string | null = null
let lastLanding: Landing | null = null

/** The landing's parameters and ad creative; one query string is parsed once. */
export function readLanding(queryString: unknown): Landing {
  const raw = typeof queryString === 'string' ? queryString : ''
  if (raw === lastRaw && lastLanding) return lastLanding
  let params: URLSearchParams
  try {
    params = new URLSearchParams(raw)
  } catch {
    params = new URLSearchParams()
  }
  const landing = { params, creative: parseCreativeName(params.get('utm_content') ?? '') }
  lastRaw = raw
  lastLanding = landing
  return landing
}

export const isLandingKey = (key: string): boolean =>
  (key.startsWith(ORIGIN_QUERY_PREFIX) && key.length > ORIGIN_QUERY_PREFIX.length) ||
  (key.startsWith(CREATIVE_PREFIX) && key.length > CREATIVE_PREFIX.length)

/**
 * A landing key's value, `undefined` when the landing did not carry it. A repeated
 * parameter reads as its first value, as ingress reads `request.query`.
 */
export function landingValue(key: string, queryString: unknown): string | undefined {
  const landing = readLanding(queryString)
  if (key.startsWith(ORIGIN_QUERY_PREFIX))
    return landing.params.get(key.slice(ORIGIN_QUERY_PREFIX.length)) ?? undefined
  if (key.startsWith(CREATIVE_PREFIX))
    return landing.creative[key.slice(CREATIVE_PREFIX.length) as CreativeField]
  return undefined
}
